import { ApolloProvider } from "@apollo/client";
import { Box, ChakraProvider } from "@chakra-ui/react";
import LoginProvider from "@components/HOC/LoginProvider/LoginProvider";
import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import * as Sentry from "@sentry/react";
import "@shopify/polaris/styles.min.css";
import "@src/assets/css/global.css";
import "@src/assets/css/overrides/overrides.css";
import "@src/assets/css/overrides/polaris.css";
import "@src/assets/css/overrides/tippy-theme-unstyled.css";
import "@src/assets/css/tailwind.css";
import { DevTools } from "@src/components/HOC/DevTools/DevTools";
import { clearEditorDrafts } from "@src/components/ui-kit/TextEditor/TextEditorDraftStorage";
import { UnexpectedError } from "@src/components/widgets/ErrorPage";
import { useRecordMyUrl } from "@src/helpers/route-history";
import { client as apolloClient } from "@src/services/apollo-client";
import { client } from "@src/services/client";
import { AppStoreContext, appStore } from "@src/stores/AppStore";
import { theme } from "@src/theme";
import { IntercomWidget } from "@src/third-party/IntercomWidget";
import { useRecordPrevUrl } from "@src/utils/hooks/useRecordPrevUrl";
import "fix-date";
import "focus-visible/dist/focus-visible";
import { configure } from "mobx";
import { useRouter } from "next/router";
import { type ReactNode, useEffect, useRef } from "react";
import { HelmetProvider } from "react-helmet-async";
import "tippy.js/dist/tippy.css";
import { Provider as UrqlProvider } from "urql";
import "../../scripts/wdyr";

configure({ enforceActions: "never" });

type AppProvidersProps = {
  readonly children: ReactNode;
};

export const AppProviders = ({ children }: AppProvidersProps) => {
  const { pathname } = useRouter();
  const toastContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    clearEditorDrafts();
  }, []);

  useRecordPrevUrl();

  useRecordMyUrl([
    "/invoices/for-invoicing",
    "/invoices/outgoing-invoices",
    "/invoices/proforma-invoices",
    "/expenses",
  ]);

  return (
    <HelmetProvider>
      <I18nProvider i18n={i18n}>
        <ApolloProvider client={apolloClient}>
          <ChakraProvider
            portalZIndex={422}
            theme={theme}
            toastOptions={{
              portalProps: {
                containerRef: toastContainerRef,
              },
            }}
          >
            <Sentry.ErrorBoundary fallback={UnexpectedError}>
              <AppStoreContext.Provider value={appStore}>
                <UrqlProvider value={client}>
                  <LoginProvider>
                    <DevTools>
                      {appStore.authStore.PUBLIC_PAGES.includes(
                        pathname,
                      ) ? null : (
                        <IntercomWidget />
                      )}
                      {children}
                    </DevTools>
                  </LoginProvider>
                </UrqlProvider>
              </AppStoreContext.Provider>
            </Sentry.ErrorBoundary>

            <Box ref={toastContainerRef} zIndex="toast" />
          </ChakraProvider>
        </ApolloProvider>
      </I18nProvider>
    </HelmetProvider>
  );
};
